<template>
  <v-container fluid style="padding: 0px">
    <!-- Hero Section -->
    <section class="hero-section">
      <v-carousel
        fluid
        :cycle="true"
        :interval="8000"
        hide-arrows
        hide-delimiters
        height="300px"
      >
        <v-carousel-item>
          <div class="hero-gradient">
            <div class="hero-content">
              <div class="hero-title-container">
                <h1 class="hero-title">Thank you for your payment!</h1>
                <p class="hero-subtitle">
                  Your subscription has been successfully activated
                </p>
              </div>
            </div>
          </div>
        </v-carousel-item>
      </v-carousel>
    </section>

    <!-- What You Can Do Next Section -->
    <v-container class="py-12 px-4">
      <v-row class="mb-8">
        <v-col cols="12" class="text-center">
          <h2 class="section-title mb-3">What You Can Do Next</h2>
          <p class="section-subtitle">Get started with these quick actions</p>
        </v-col>
      </v-row>

      <!-- Quick Actions -->
      <v-row justify="center">
        <v-col cols="12" md="4" sm="6">
          <v-card
            class="action-card"
            elevation="2"
            hover
            @click="goToWorkspaces"
          >
            <v-card-text class="text-center pa-6">
              <v-icon size="64" color="primary" class="mb-4"
                >mdi-folder-multiple</v-icon
              >
              <h3 class="card-title mb-2">Create Your Workspace</h3>
              <p class="card-description">
                Organise your bookmarks into workspaces and collaborate with
                your team
              </p>
            </v-card-text>
            <v-card-actions class="justify-center pb-4">
              <v-btn color="primary" outlined>Get Started</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <v-col cols="12" md="4" sm="6">
          <v-card
            class="action-card"
            elevation="2"
            hover
            @click="goToAiRecommendations"
          >
            <v-card-text class="text-center pa-6">
              <v-icon size="64" color="success" class="mb-4">mdi-brain</v-icon>
              <h3 class="card-title mb-2">AI Recommendations</h3>
              <p class="card-description">
                Discover personalised content recommendations powered by AI
              </p>
            </v-card-text>
            <v-card-actions class="justify-center pb-4">
              <v-btn color="success" outlined>Explore AI</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <v-col cols="12" md="4" sm="6">
          <v-card class="action-card" elevation="2" hover @click="goToRssFeeds">
            <v-card-text class="text-center pa-6">
              <v-icon size="64" color="info" class="mb-4">mdi-rss</v-icon>
              <h3 class="card-title mb-2">RSS Feeds</h3>
              <p class="card-description">
                Subscribe to RSS feeds and stay updated with your favourite
                content
              </p>
            </v-card-text>
            <v-card-actions class="justify-center pb-4">
              <v-btn color="info" outlined>Browse Feeds</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <v-col cols="12" md="4" sm="6">
          <v-card
            class="action-card"
            elevation="2"
            hover
            @click="goToAiSummary"
          >
            <v-card-text class="text-center pa-6">
              <v-icon size="64" color="purple" class="mb-4"
                >mdi-text-box-outline</v-icon
              >
              <h3 class="card-title mb-2">AI Summary</h3>
              <p class="card-description">
                Get instant AI-generated summaries of your bookmarks and ask
                custom questions
              </p>
            </v-card-text>
            <v-card-actions class="justify-center pb-4">
              <v-btn color="purple" outlined>Try It Now</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <v-col cols="12" md="4" sm="6">
          <v-card
            class="action-card"
            elevation="2"
            hover
            @click="goToAiCategoryOrganiser"
          >
            <v-card-text class="text-center pa-6">
              <v-icon size="64" color="cyan" class="mb-4">mdi-auto-fix</v-icon>
              <h3 class="card-title mb-2">AI Category Organiser</h3>
              <p class="card-description">
                Automatically organise bookmarks into smart, logical categories
                with AI
              </p>
            </v-card-text>
            <v-card-actions class="justify-center pb-4">
              <v-btn color="cyan" outlined>Organise Now</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- What's Included Section -->
      <v-row class="mt-12 mb-8">
        <v-col cols="12" class="text-center">
          <h2 class="section-title mb-3">What's Included in Your Plan</h2>
          <p class="section-subtitle">
            Everything you need to organise, share, and discover amazing content
          </p>
        </v-col>
      </v-row>

      <v-row justify="center">
        <v-col cols="12" md="10" lg="12">
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-card class="benefit-card pa-4" flat>
                <div class="text-center">
                  <v-icon size="48" color="primary" class="mb-2"
                    >mdi-infinity</v-icon
                  >
                  <h4 class="benefit-title mb-1">Unlimited</h4>
                  <p class="benefit-description">
                    Bookmarks & Workspaces
                  </p>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-card class="benefit-card pa-4" flat>
                <div class="text-center">
                  <v-icon size="48" color="success" class="mb-2"
                    >mdi-account-group</v-icon
                  >
                  <h4 class="benefit-title mb-1">Team Collaboration</h4>
                  <p class="benefit-description">
                    Share with unlimited members
                  </p>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-card class="benefit-card pa-4" flat>
                <div class="text-center">
                  <v-icon size="48" color="info" class="mb-2">mdi-robot</v-icon>
                  <h4 class="benefit-title mb-1">AI-Powered</h4>
                  <p class="benefit-description">
                    Smart recommendations
                  </p>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-card class="benefit-card pa-4" flat>
                <div class="text-center">
                  <v-icon size="48" color="warning" class="mb-2"
                    >mdi-shield-check</v-icon
                  >
                  <h4 class="benefit-title mb-1">Secure & Private</h4>
                  <p class="benefit-description">
                    Enterprise-grade security
                  </p>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-card class="benefit-card pa-4" flat>
                <div class="text-center">
                  <v-icon size="48" color="purple" class="mb-2"
                    >mdi-text-box-outline</v-icon
                  >
                  <h4 class="benefit-title mb-1">AI Summary</h4>
                  <p class="benefit-description">
                    Instant bookmark summaries
                  </p>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-card class="benefit-card pa-4" flat>
                <div class="text-center">
                  <v-icon size="48" color="cyan" class="mb-2"
                    >mdi-auto-fix</v-icon
                  >
                  <h4 class="benefit-title mb-1">AI Organiser</h4>
                  <p class="benefit-description">
                    Smart category sorting
                  </p>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-card class="benefit-card pa-4" flat>
                <div class="text-center">
                  <v-icon size="48" color="blue" class="mb-2"
                    >mdi-puzzle-outline</v-icon
                  >
                  <h4 class="benefit-title mb-1">Browser Extension</h4>
                  <p class="benefit-description">
                    Chrome & Edge integration
                  </p>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <!-- Bring Your Own Key Section -->
      <v-row class="mt-12 mb-8">
        <v-col cols="12" class="text-center">
          <h2 class="section-title mb-3">Use Your Own AI Key with BYOK</h2>
          <p class="section-subtitle">
            Bring Your Own Key gives you more control over the AI features in
            LinkBook by using your own OpenRouter account and API key.
          </p>
        </v-col>
      </v-row>

      <v-row justify="center">
        <v-col cols="12" md="10" lg="9">
          <v-card class="byok-card" elevation="2">
            <v-card-text class="pa-6 pa-md-8">
              <div class="d-flex align-start">
                <v-icon size="48" color="primary" class="mr-4"
                  >mdi-key-variant</v-icon
                >
                <div>
                  <h3 class="byok-title mb-2">Bring Your Own Key (BYOK)</h3>
                  <p class="byok-description mb-6">
                    Use your own OpenRouter API key for AI Recommendations and
                    AI Summary. Your key is encrypted and stored securely
                    against your LinkBook account. LinkBook does not provide a
                    shared AI provider key.
                  </p>
                </div>
              </div>

              <v-alert type="info" outlined border="left" class="mb-6">
                OpenRouter usage and charges are managed through your OpenRouter
                account. LinkBook does not provide or manage your OpenRouter
                billing.
              </v-alert>

              <h4 class="byok-steps-title mb-4">How to set up BYOK</h4>
              <v-list class="byok-steps-list pa-0" color="transparent">
                <v-list-item class="px-0">
                  <v-list-item-avatar color="primary" class="white--text">
                    1
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium"
                      >Create an OpenRouter account or sign
                      in</v-list-item-title
                    >
                    <v-list-item-subtitle
                      >Visit OpenRouter's key management page to get
                      started.</v-list-item-subtitle
                    >
                  </v-list-item-content>
                </v-list-item>

                <v-list-item class="px-0">
                  <v-list-item-avatar color="primary" class="white--text">
                    2
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium"
                      >Create and copy your API key</v-list-item-title
                    >
                    <v-list-item-subtitle
                      >Copy the key when it is created and keep it
                      private.</v-list-item-subtitle
                    >
                  </v-list-item-content>
                </v-list-item>

                <v-list-item class="px-0">
                  <v-list-item-avatar color="primary" class="white--text">
                    3
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium"
                      >Add the key to your LinkBook profile</v-list-item-title
                    >
                    <v-list-item-subtitle
                      >Open your profile, find the BYOK section, paste your key,
                      and select Save key. You can manage your subscription and
                      billing details in the Subscription tab of your
                      profile.</v-list-item-subtitle
                    >
                  </v-list-item-content>
                </v-list-item>

                <v-list-item class="px-0">
                  <v-list-item-avatar color="primary" class="white--text">
                    4
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium"
                      >Start using your AI features</v-list-item-title
                    >
                    <v-list-item-subtitle
                      >Your saved key will be used for all LinkBook AI features.
                      You can update or remove it from your profile at any
                      time.</v-list-item-subtitle
                    >
                  </v-list-item-content>
                </v-list-item>
              </v-list>

              <div class="byok-actions mt-6">
                <v-btn
                  color="primary"
                  class="mr-3 mb-2"
                  href="https://openrouter.ai/keys"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <v-icon left>mdi-open-in-new</v-icon>
                  Create OpenRouter Key
                </v-btn>
                <v-btn
                  color="primary"
                  outlined
                  class="mb-2"
                  @click="goToProfile"
                >
                  <v-icon left>mdi-account-cog</v-icon>
                  Open Profile Settings
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Help & Resources -->
      <v-row class="mt-12">
        <v-col cols="12" class="text-center">
          <v-card class="help-card pa-6" outlined>
            <v-icon size="48" color="primary" class="mb-3"
              >mdi-help-circle</v-icon
            >
            <h3 class="mb-3">Need Help Getting Started?</h3>
            <p class="mb-4">
              Check out our resources or contact our support team
            </p>
            <v-btn color="primary" @click="contactSupport">
              <v-icon left>mdi-email</v-icon>
              Contact Support
            </v-btn>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
//import WorkspaceTutorial from '@/components/workspace/WorkspaceTutorial.vue'

export default {
  name: 'PaymentSuccess',
  // components: {
  //   WorkspaceTutorial
  // },
  metaInfo: {
    title: 'Payment Successful - Welcome to Your New Features',
    titleTemplate: '%s | LinkBook.io',
    meta: [
      {
        name: 'description',
        content:
          'Thank you for subscribing! Access AI Summary, AI Category Organiser, team workspaces, and more premium features.'
      }
    ]
  },
  methods: {
    goToWorkspaces() {
      window.open('/teams/workspace', '_blank')
    },
    goToAiRecommendations() {
      window.open('/ai-recommendations', '_blank')
    },
    goToRssFeeds() {
      window.open('/rss-feeds', '_blank')
    },
    goToAiSummary() {
      window.open('/about-ai-summary/', '_blank')
    },
    goToAiCategoryOrganiser() {
      window.open('/about-ai-category-organiser/', '_blank')
    },
    goToProfile() {
      window.open('/profile', '_blank')
    },
    contactSupport() {
      window.open('mailto:info@linkbook.io', '_blank')
    }
  }
}
</script>

<style lang="scss" scoped>
.hero-section {
  position: relative;
  overflow: hidden;
}

.hero-gradient {
  background: url('/img/banner/networks-web.webp');
  background-size: cover;
  background-position: center;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
  }
}

.hero-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  z-index: 1;
}

.hero-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: white;

  @media (max-width: 600px) {
    font-size: 2rem;
  }
}

.hero-subtitle {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  margin: 0 auto;
}

// What You Can Do Next Section
.section-title {
  font-size: 2.25rem;
  font-weight: 700;
  color: #333;

  @media (max-width: 600px) {
    font-size: 1.75rem;
  }
}

.section-subtitle {
  font-size: 1.1rem;
  color: #666;
  max-width: 700px;
  margin: 0 auto;
}

.action-card {
  height: 100%;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15) !important;
  }
}

.card-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

.card-description {
  font-size: 1rem;
  color: #666;
  line-height: 1.6;
}

// Benefit Cards
.benefit-card {
  height: 100%;
  background-color: transparent;

  @media (max-width: 960px) {
    margin-bottom: 1rem;
  }
}

.benefit-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
}

.benefit-description {
  font-size: 0.9rem;
  color: #666;
  line-height: 1.4;
  margin: 0;
}

// BYOK Card
.byok-card {
  border-top: 4px solid #1976d2;
}

.byok-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

.byok-description {
  color: #666;
  line-height: 1.6;
  margin-bottom: 0;
}

.byok-steps-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
}

.byok-steps-list {
  .v-list-item {
    min-height: 64px;
  }

  .v-list-item-avatar {
    align-self: flex-start;
    justify-content: center;
    min-width: 32px;
    width: 32px;
    height: 32px;
    margin-top: 4px;
    margin-right: 16px;
  }

  .v-list-item-title {
    color: #333;
    white-space: normal;
  }

  .v-list-item-subtitle {
    color: #666;
    line-height: 1.5;
    white-space: normal;
  }
}

.byok-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

// Help Card
.help-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border: none !important;

  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #333;
  }

  p {
    font-size: 1rem;
    color: #666;
  }
}

// Responsive adjustments
@media (max-width: 960px) {
  .action-card {
    margin-bottom: 1rem;
  }
}
</style>
